import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { TopicListActions } from './topic-list.actions';
import { TopicService } from '../../features/market-comparison/services/topic.service';
import { SUCCESS_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { RootState } from '../root-state.model';
import { TopicActions } from '../topic/topic.actions';

@Injectable()
export class TopicListEffects {
  private readonly router = inject(Router);
  private readonly topicService = inject(TopicService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly webSocketService = inject(WebSocketService);

  private readonly notifications: PushNotificationRecord = {
    [TopicListActions.DeleteTopicSuccess.type]: { message: 'Topic deleted', isSuccess: true },
    [TopicListActions.DeleteTopicFail.type]: {
      message: 'Failed to delete the topic',
      isSuccess: false,
    },
    [TopicListActions.CloneModelSuccess.type]: { message: 'Topic cloned', isSuccess: true },
    [TopicListActions.CloneModelFail.type]: {
      message: 'Failed to clone the topic',
      isSuccess: false,
    },
  };

  LoadTopics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.LoadTopics),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.topicService.getAllTopicsAndOrders().pipe(
          map((result) => TopicListActions.LoadTopicsSuccess(result)),
          reportEffectError(TopicListActions.LoadTopicsFail)
        )
      )
    )
  );

  createTopicEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.CreateTopic),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.topicService.create(action.topic).pipe(
          map((topic) => TopicListActions.CreateTopicSuccess({ topic })),
          reportEffectError(TopicListActions.CreateTopicFail)
        )
      )
    )
  );

  deleteTopicEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.DeleteTopic),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.topicService.delete(action.id).pipe(
          map((res) =>
            TopicListActions.DeleteTopicSuccess({ id: action.id, redirect: action.redirect })
          ),
          reportEffectError(TopicListActions.DeleteTopicFail)
        )
      )
    )
  );

  deleteTopicSuccessEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.DeleteTopicSuccess),
      withLatestFrom(this.store),
      tap(([action, store]) => {
        if (action.redirect) {
          this.router.navigate(['/', SemerisRoutes.topic.root]);
        }
      }),
      map(([action, store]) =>
        TopicListActions.LoadTopicsSuccess({
          list: store.topicList.topics,
          showInMenuIds: store.topicList.showInMenuIds,
          orderedTopicIds: store.topicList.orderedTopicIds,
        })
      )
    )
  );

  saveTopicSuccessEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicActions.SaveTopicSuccess),
      withLatestFrom(this.store),
      map(([action, store]) =>
        TopicListActions.LoadTopicsSuccess({
          list: store.topicList.topics,
          showInMenuIds: store.topicList.showInMenuIds,
          orderedTopicIds: store.topicList.orderedTopicIds,
        })
      )
    )
  );

  createTopicSuccessNavigateEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.CreateTopicSuccess),
      tap((action) =>
        this.router.navigate(['/', SemerisRoutes.marketComparison.root, action.topic.id])
      ),
      withLatestFrom(this.store),
      map(([action, store]) =>
        TopicListActions.LoadTopicsSuccess({
          list: store.topicList.topics,
          showInMenuIds: store.topicList.showInMenuIds,
          orderedTopicIds: store.topicList.orderedTopicIds,
        })
      )
    )
  );

  reorderTopicsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.ReorderTopics, TopicListActions.SetMenuTopics),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.topicService
          .updateSort(store.topicList.orderedTopicIds, store.topicList.showInMenuIds)
          .pipe(
            map((result) => TopicListActions.TopicsMetaUpdateSuccess(result)),
            reportEffectError(TopicListActions.TopicsMetaUpdateTopicsFail)
          );
      })
    )
  );

  cloneModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.CloneModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.topicService.clone(action.id).pipe(
          map((topic) => TopicListActions.CloneModelSuccess({ topic })),
          reportEffectError(TopicListActions.CloneModelFail)
        )
      )
    )
  );

  cloneModelSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TopicListActions.CloneModelSuccess),
        map((action) =>
          this.router.navigate(['/', SemerisRoutes.marketComparison.root, action.topic.id])
        )
      ),
    { dispatch: false }
  );

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Topic,
    () => TopicListActions.LoadTopics()
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  templateModalClosedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      filter((action) => action.modalId === 'edit-topic-list' && !!action.data),
      map((action) =>
        TopicListActions.SaveTopicDetails({
          data: action.data,
        })
      )
    )
  );

  SaveTopicDetails$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.SaveTopicDetails),
      withLatestFrom(this.store),
      map(([action, store]) => ({
        topic: store.topicList.topics.find((t) => t.id === action.data.id),
        action,
      })),
      filter(({ topic }) => !!topic && topic.mutable),
      switchMap(({ action, topic }) => {
        return this.topicService
          .update(
            {
              ...topic,
              ...action.data,
              name: action.data.displayName,
            },
            true,
            !!topic.canShare,
            !!topic.canPublish
          )
          .pipe(
            map((t) =>
              TopicListActions.SaveTopicDetailsSuccess({ topic: t, showNotification: true })
            ),
            reportEffectError(TopicListActions.SaveTopicDetailsFail)
          );
      })
    )
  );

  saveTopicSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopicListActions.SaveTopicDetailsSuccess),
      filter((action) => !!action.showNotification),
      map((action) =>
        AppActions.PushNotification({
          message: `Topic updated`,
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );
}
